import React, { useState, useEffect, useRef, useCallback } from "react";
import "./TrafficEscape.css";

const TrafficEscape = ({ onBack, isPaused }) => {
    const canvasRef = useRef(null);
    const [score, setScore] = useState(0);
    const [gameOver, setGameOver] = useState(false);
    const [gameStarted, setGameStarted] = useState(false);

    const CAN_W = 400;
    const CAN_H = 600;
    const PLAYER_W = 40;
    const PLAYER_H = 70;

    const game = useRef({
        player: { x: CAN_W / 2 - 20, y: CAN_H - 120, lane: 1 },
        traffic: [],
        lastSpawn: 0,
        lastFrame: 0,
        speed: 5
    });

    const spawnCar = () => {
        const lane = Math.floor(Math.random() * 3);
        const x = 50 + lane * 100 + 10;
        const speed = game.current.speed + Math.random() * 3;
        const colors = ["#ef4444", "#3b82f6", "#a855f7", "#f97316", "#eab308"];
        return { x, y: -100, speed, color: colors[Math.floor(Math.random() * colors.length)] };
    };

    const update = useCallback((time) => {
        if (!gameStarted || gameOver) return;
        if (isPaused) {
            game.current.lastFrame = time;
            requestAnimationFrame(update);
            return;
        }
        const dt = (time - game.current.lastFrame) / 16;
        game.current.lastFrame = time;

        // Speed up
        game.current.speed += 0.001 * dt;

        // Spawn
        if (time - game.current.lastSpawn > 1200 / (game.current.speed / 5)) {
            game.current.traffic.push(spawnCar());
            game.current.lastSpawn = time;
        }

        // Update Traffic
        game.current.traffic.forEach(car => {
            car.y += car.speed * dt;
            
            // Collision
            const px = 50 + game.current.player.lane * 100 + 10;
            const py = game.current.player.y;
            if (px < car.x + PLAYER_W && px + PLAYER_W > car.x &&
                py < car.y + PLAYER_H && py + PLAYER_H > car.y) {
                setGameOver(true);
            }
        });
        game.current.traffic = game.current.traffic.filter(car => car.y < CAN_H + 100);

        setScore(s => s + 1);
        render();
        requestAnimationFrame(update);
    }, [gameStarted, gameOver, isPaused]);

    const render = () => {
        const ctx = canvasRef.current?.getContext("2d");
        if (!ctx) return;
        
        // Background
        ctx.fillStyle = "#0f172a";
        ctx.fillRect(0, 0, CAN_W, CAN_H);
        
        // Road
        ctx.fillStyle = "#1e293b";
        ctx.fillRect(40, 0, CAN_W - 80, CAN_H);
        
        // Lanes
        ctx.strokeStyle = "rgba(255,255,255,0.15)";
        ctx.setLineDash([30, 30]);
        ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(CAN_W / 3 + 15, 0); ctx.lineTo(CAN_W / 3 + 15, CAN_H); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(CAN_W * 2/3 - 15, 0); ctx.lineTo(CAN_W * 2/3 - 15, CAN_H); ctx.stroke();
        ctx.setLineDash([]);

        // Player (Cyber Car)
        const px = 50 + game.current.player.lane * 100 + 10;
        ctx.fillStyle = "#10b981";
        ctx.shadowBlur = 15; ctx.shadowColor = "#10b981";
        ctx.beginPath();
        ctx.roundRect(px, game.current.player.y, PLAYER_W, PLAYER_H, 8);
        ctx.fill();
        
        // Traffic
        ctx.shadowBlur = 10;
        game.current.traffic.forEach(car => {
            ctx.fillStyle = car.color;
            ctx.shadowColor = car.color;
            ctx.beginPath();
            ctx.roundRect(car.x, car.y, PLAYER_W, PLAYER_H, 8);
            ctx.fill();
        });
        ctx.shadowBlur = 0;
    };

    useEffect(() => {
        if (gameStarted) {
            game.current.lastFrame = performance.now();
            requestAnimationFrame(update);
        } else {
            render();
        }
    }, [gameStarted, update]);

    const handleInput = useCallback((dir) => {
        if (!gameStarted || gameOver) return;
        if (dir === "left") {
            game.current.player.lane = Math.max(0, game.current.player.lane - 1);
        } else {
            game.current.player.lane = Math.min(2, game.current.player.lane + 1);
        }
    }, [gameStarted, gameOver]);

    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === "ArrowLeft") handleInput("left");
            if (e.key === "ArrowRight") handleInput("right");
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [handleInput]);

    const startGame = () => {
        setScore(0);
        setGameOver(false);
        setGameStarted(true);
        game.current.traffic = [];
        game.current.speed = 5;
        game.current.player.lane = 1;
    };

    return (
        <div className="te_container">
            <div className="te_header">
                <button onClick={onBack} className="te_back">← Hub</button>
                <div className="te_score">SCORE: {score}</div>
            </div>
            
            <div className="te_game-wrapper">
                <canvas 
                    ref={canvasRef} 
                    width={CAN_W} 
                    height={CAN_H} 
                    onTouchStart={(e) => {
                        const touch = e.touches[0];
                        const rect = canvasRef.current.getBoundingClientRect();
                        const x = touch.clientX - rect.left;
                        if (x < rect.width / 2) handleInput("left");
                        else handleInput("right");
                    }}
                    onMouseDown={(e) => {
                        const rect = canvasRef.current.getBoundingClientRect();
                        const x = e.clientX - rect.left;
                        if (x < rect.width / 2) handleInput("left");
                        else handleInput("right");
                    }}
                />

                {!gameStarted && !gameOver && (
                    <div className="te_overlay">
                        <div className="te_modal">
                            <h1>TRAFFIC<span>ESCAPE</span></h1>
                            <p>DODGE THE TRAFFIC AT LIGHT SPEED</p>
                            <button className="te_play-btn" onClick={startGame}>START ENGINE</button>
                            <div className="te_hint">Tap Left/Right to Switch Lanes</div>
                        </div>
                    </div>
                )}

                {gameOver && (
                    <div className="te_overlay">
                        <div className="te_modal">
                            <h1 className="is-red">WRECKED</h1>
                            <div className="te_final-score">
                                <label>FINAL SCORE</label>
                                <span>{score}</span>
                            </div>
                            <button className="te_play-btn" onClick={startGame}>RETRY</button>
                            <button className="te_exit-btn" onClick={onBack}>EXIT HUB</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TrafficEscape;
